const STORAGE_PREFIX = 'messenger-v2-call-lifecycle';
const STORAGE_VERSION = 1;
const TERMINAL_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_TERMINAL_CALLS = 250;
const RECOVERY_COOLDOWN_MS = 750;

const TERMINAL_STATUSES = new Set(['ended', 'cancelled', 'failed', 'declined', 'missed']);
const INVITE_SIGNAL_TYPES = new Set(['invite', 'offer', 'sfu-invite']);

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toLifecycleVersion = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : null;
};

const normalizeStatus = (value) => toStringOrNull(value)?.toLowerCase() || null;

const normalizeCall = (value = null) => {
  const source = value?.conversation_call || value?.conversationCall || value?.call || value;
  if (!source || typeof source !== 'object') return null;

  const callId = toStringOrNull(source.id ?? source.call_id ?? source.callId ?? null);
  const conversationId = toStringOrNull(source.conversation_id ?? source.conversationId ?? null);
  const status = normalizeStatus(source.status ?? source.call_status ?? source.callStatus ?? null);
  if (!callId || !conversationId || !status) return null;

  return {
    callId,
    conversationId,
    status,
    lifecycleVersion: toLifecycleVersion(
      source.lifecycle_version ?? source.lifecycleVersion ?? source.state_version ?? null,
    ),
    endedAt: toStringOrNull(source.ended_at ?? source.endedAt ?? null),
    raw: source,
  };
};

const normalizeStoredTerminalCalls = (value, now) => {
  const items = Array.isArray(value) ? value : [];
  return items
    .map((item) => ({
      callId: toStringOrNull(item?.callId),
      conversationId: toStringOrNull(item?.conversationId),
      lifecycleVersion: toLifecycleVersion(item?.lifecycleVersion),
      recordedAt: Number(item?.recordedAt) || 0,
    }))
    .filter(
      (item) =>
        item.callId &&
        item.conversationId &&
        item.recordedAt > 0 &&
        now - item.recordedAt <= TERMINAL_TTL_MS,
    )
    .slice(-MAX_TERMINAL_CALLS);
};

const buildStorageKey = (userId) => {
  const normalizedUserId = toStringOrNull(userId);
  return normalizedUserId ? `${STORAGE_PREFIX}:${normalizedUserId}` : null;
};

export const createMessengerV2CallLifecycleCoordinator = ({
  userId,
  loadActiveCalls,
  loadCall,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
  now = () => Date.now(),
} = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  const storageKey = buildStorageKey(normalizedUserId);
  const terminalCalls = new Map();
  const authoritativeCalls = new Map();
  const verificationFlights = new Map();
  let recoveryFlight = null;
  let lastRecoveryAt = 0;
  let recoveryQueued = false;
  let recoveryStarted = false;

  const persistTerminalCalls = () => {
    if (!storageKey || !storage?.setItem) return;
    const items = Array.from(terminalCalls.values())
      .sort((left, right) => left.recordedAt - right.recordedAt)
      .slice(-MAX_TERMINAL_CALLS);
    try {
      storage.setItem(storageKey, JSON.stringify({ version: STORAGE_VERSION, items }));
    } catch {}
  };

  const hydrateTerminalCalls = () => {
    if (!storageKey || !storage?.getItem) return;
    try {
      const parsed = JSON.parse(storage.getItem(storageKey) || 'null');
      if (parsed?.version !== STORAGE_VERSION) return;
      normalizeStoredTerminalCalls(parsed.items, now()).forEach((item) => {
        terminalCalls.set(item.callId, item);
      });
      persistTerminalCalls();
    } catch {}
  };

  const rememberTerminal = ({
    callId,
    conversationId,
    lifecycleVersion = null,
    recordedAt = now(),
  } = {}) => {
    const normalizedCallId = toStringOrNull(callId);
    const normalizedConversationId = toStringOrNull(conversationId);
    if (!normalizedCallId || !normalizedConversationId) return false;

    const previous = terminalCalls.get(normalizedCallId);
    const nextVersion = toLifecycleVersion(lifecycleVersion);
    const authoritativeCall = authoritativeCalls.get(normalizedCallId);
    if (
      nextVersion != null &&
      authoritativeCall?.lifecycleVersion != null &&
      authoritativeCall.lifecycleVersion > nextVersion
    ) {
      return false;
    }
    if (
      previous &&
      previous.conversationId === normalizedConversationId &&
      (previous.lifecycleVersion ?? -1) > (nextVersion ?? -1)
    ) {
      return false;
    }

    terminalCalls.set(normalizedCallId, {
      callId: normalizedCallId,
      conversationId: normalizedConversationId,
      lifecycleVersion: nextVersion ?? previous?.lifecycleVersion ?? null,
      recordedAt: Number(recordedAt) || now(),
    });
    authoritativeCalls.delete(normalizedCallId);
    persistTerminalCalls();
    return true;
  };

  const observeAuthoritativeCall = (value) => {
    const call = normalizeCall(value);
    if (!call) return null;

    if (TERMINAL_STATUSES.has(call.status)) {
      rememberTerminal(call);
    } else if (!terminalCalls.has(call.callId)) {
      authoritativeCalls.set(call.callId, call);
    }
    return call;
  };

  const observeAuthoritativeEvent = (payload = {}) => {
    const callId = toStringOrNull(payload.call_id ?? payload.callId ?? null);
    const conversationId = toStringOrNull(
      payload.conversation_id ?? payload.conversationId ?? null,
    );
    const status = normalizeStatus(
      payload.call_status ?? payload.callStatus ?? payload.action ?? payload.event ?? null,
    );
    if (!callId || !conversationId || !status) return null;
    return observeAuthoritativeCall({
      id: callId,
      conversation_id: conversationId,
      status,
      lifecycle_version:
        payload.lifecycle_version ?? payload.lifecycleVersion ?? payload.state_version ?? null,
      ended_at: payload.ended_at ?? payload.endedAt ?? null,
    });
  };

  const recover = ({ force = false, queueIfRunning = false } = {}) => {
    if (recoveryFlight) {
      recoveryQueued = recoveryQueued || queueIfRunning;
      return recoveryFlight;
    }
    if (typeof loadActiveCalls !== 'function') return Promise.resolve(null);

    const elapsed = now() - lastRecoveryAt;
    if (!force && !queueIfRunning && lastRecoveryAt > 0 && elapsed < RECOVERY_COOLDOWN_MS) {
      return Promise.resolve(null);
    }

    const runRecovery = () =>
      Promise.resolve()
        .then(() => {
          recoveryStarted = true;
          return loadActiveCalls();
        })
        .then((payload) => {
          lastRecoveryAt = now();
          authoritativeCalls.clear();
          const activeCalls = Array.isArray(payload?.conversation_calls)
            ? payload.conversation_calls
            : [];
          const pendingSummaries = Array.isArray(payload?.pending_call_summaries)
            ? payload.pending_call_summaries
            : [];
          activeCalls.forEach(observeAuthoritativeCall);
          pendingSummaries.forEach(observeAuthoritativeCall);
          return payload;
        });

    const request = runRecovery()
      .then(async (payload) => {
        if (!recoveryQueued) return payload;
        recoveryQueued = false;
        recoveryStarted = false;
        return runRecovery();
      })
      .finally(() => {
        if (recoveryFlight === request) {
          recoveryFlight = null;
          recoveryQueued = false;
          recoveryStarted = false;
        }
      });
    recoveryFlight = request;
    return request;
  };

  const verifyCall = ({ callId, conversationId }) => {
    const normalizedCallId = toStringOrNull(callId);
    const normalizedConversationId = toStringOrNull(conversationId);
    if (!normalizedCallId || !normalizedConversationId || typeof loadCall !== 'function') {
      return Promise.resolve({ decision: 'defer', call: null });
    }
    if (terminalCalls.has(normalizedCallId)) {
      return Promise.resolve({ decision: 'ignore', call: null });
    }
    if (verificationFlights.has(normalizedCallId)) {
      return verificationFlights.get(normalizedCallId);
    }

    const request = Promise.resolve()
      .then(() => loadCall({ callId: normalizedCallId, conversationId: normalizedConversationId }))
      .then((payload) => {
        const call = observeAuthoritativeCall(payload);
        if (!call || call.conversationId !== normalizedConversationId) {
          return { decision: 'defer', call };
        }
        return {
          decision: ['preparing', 'ringing'].includes(call.status) ? 'accept' : 'ignore',
          call,
        };
      })
      .catch((error) => {
        if (error?.status === 404 || error?.status === 410) {
          rememberTerminal({ callId: normalizedCallId, conversationId: normalizedConversationId });
          return { decision: 'ignore', call: null };
        }
        return { decision: 'defer', call: null };
      })
      .finally(() => {
        if (verificationFlights.get(normalizedCallId) === request) {
          verificationFlights.delete(normalizedCallId);
        }
      });
    verificationFlights.set(normalizedCallId, request);
    return request;
  };

  const classifySignal = async ({ signal = null } = {}) => {
    const callId = toStringOrNull(signal?.callId ?? signal?.call_id ?? null);
    const conversationId = toStringOrNull(
      signal?.conversationId ?? signal?.conversation_id ?? null,
    );
    const signalType = normalizeStatus(
      signal?.signalType ?? signal?.signal_type ?? signal?.type ?? null,
    );
    if (!callId || !conversationId || !signalType) return { decision: 'ignore', call: null };

    if (TERMINAL_STATUSES.has(signalType) || ['end', 'cancel', 'timeout'].includes(signalType)) {
      const remembered = rememberTerminal({
        callId,
        conversationId,
        lifecycleVersion: signal.lifecycleVersion ?? signal.lifecycle_version ?? null,
      });
      return {
        decision: remembered || terminalCalls.has(callId) ? 'accept' : 'ignore',
        call: authoritativeCalls.get(callId) || null,
      };
    }
    if (terminalCalls.has(callId)) return { decision: 'ignore', call: null };
    if (!INVITE_SIGNAL_TYPES.has(signalType)) return { decision: 'accept', call: null };

    const authoritativeCall = authoritativeCalls.get(callId);
    if (
      authoritativeCall?.conversationId === conversationId &&
      authoritativeCall.status === 'ringing'
    ) {
      return { decision: 'accept', call: authoritativeCall };
    }
    const verification = await verifyCall({ callId, conversationId });
    if (verification.decision === 'defer') {
      const signalAt = Date.parse(toStringOrNull(signal?.at) || '');
      const isFreshSignal = Number.isFinite(signalAt) && now() - signalAt <= RECOVERY_COOLDOWN_MS;
      if (isFreshSignal) {
        return { decision: 'accept', call: null, provisional: true };
      }
    }
    return verification;
  };

  hydrateTerminalCalls();

  return {
    userId: normalizedUserId,
    classifySignal,
    observeAuthoritativeCall,
    observeAuthoritativeEvent,
    recover,
    rememberTerminal,
    // A successful authenticated rejoin supersedes this device's previous Ignore/Leave.
    resumeJoinedGroupCall: (payload) => {
      const call = normalizeCall(payload);
      if (!call || call.raw.conversation_kind !== 'group' || call.status !== 'active') return false;
      const previous = terminalCalls.get(call.callId);
      if ((previous?.lifecycleVersion ?? -1) > (call.lifecycleVersion ?? -1)) return false;
      terminalCalls.delete(call.callId);
      authoritativeCalls.set(call.callId, call);
      persistTerminalCalls();
      return true;
    },
    isTerminal: (callId) => terminalCalls.has(toStringOrNull(callId)),
  };
};

export default createMessengerV2CallLifecycleCoordinator;
